// utils
import { fromJS, List, Map, updateIn } from 'immutable';
import { combineActions, createAction, handleActions } from 'redux-actions';
import { DEFAULT_ITEM_STATUS_GROUPS } from '../../containers/Inventory/itemStatusGroups';
// api
import api from '../../utils/api';
import { SHIPMENT_TRANSFERRING_STATE } from '../../utils/common';
import formatter from '../../utils/formatter';
// const
import {
  COMPLETE_REQUEST_TRANSFER_SHIPMENTS,
  CREATE_REQUEST_TRANSFER_SHIPMENT,
} from './serviceRequests';
import { normalizeImages } from './utils/helpers';

export const defaultParams = fromJS({
  page: 1,
  filter: {
    'q[date_received_at_gteq]': null,
    'q[date_received_at_lteq]': null,
    'q[has_customs_declarations]': null,
    'q[tracking_number_contains]': null,
    'q[vendor_eq]': null,
    'q[weight_gteq]': null,
    'q[weight_lteq]': null,
  },
});

// Shallow `Map` on purpose: `filter` and `groups` stay plain JS, matching what
// searchFormik dispatches, so the saga can spread the filter straight into the
// request params.
export const defaultParamsInventoryItems = Map({
  page: 1,
  filter: {},
  groups: DEFAULT_ITEM_STATUS_GROUPS,
});

// One entry per category shown in the items list. Each category is fetched by
// its own status query, so it carries its own total and page cursor.
// `filteredOut` counts the fetched rows hidden client-side (non-"I" SKUs), so
// `total` can stay consistent with what is actually shown.
export const defaultInventoryItemsGroup = fromJS({
  items: [],
  total: 0,
  page: 1,
  filteredOut: 0,
});

export const defaultParamsExpected = fromJS({
  page: 1,
});

export const initialState = fromJS({
  isLoading: false,
  isShipmentLoading: false,
  items: [],
  // Transferring shipments sit above the regular list and page on their own:
  // the sentinel loads their next page until `transferringTotal` is reached,
  // and only then starts paging the regular list.
  transferringItems: [],
  transferringTotal: 0,
  transferringPage: 1,
  selected: [],
  totalAmount: 0,
  hasItems: false,
  totalWeight: 0,
  params: defaultParams,
  detailedShipment: {},
  tempCustomsDeclarationIsInventoryItem: false,
  tempDetailedItemId: null,
  tempDetailedShipmentId: null,
  isShopsLoading: false,
  shops: [],
  expectedShops: [],
  isExpectedShopsLoading: false,
  inventoryItemsGroups: {},
  inventoryItemsFilter: {},
  expectedItemsSearch: [],
  isLoadingCustomsDeclaration: false,
  tempExpectedItemId: null,
  expectedItemsTotalAmount: 0,
  activeStorageBranch: null,
});

// const
export const ADD_PARCELS_TO_CART = 'storage/ADD_PARCELS_TO_CART';
export const FINALISE_ADD_PARCELS_TO_CART =
  'storage/FINALISE_ADD_PARCELS_TO_CART';
export const GET_STORAGE = 'storage/GET_STORAGE';
export const SET_ACTIVE_STORAGE_BRANCH = 'storage/SET_ACTIVE_STORAGE_BRANCH';
export const GET_INVENTORY_ITEMS = 'storage/GET_INVENTORY_ITEMS';
export const GET_INVENTORY_ITEMS_PAGE = 'storage/GET_INVENTORY_ITEMS_PAGE';
export const GET_EXPECTED_ITEMS = 'storage/GET_EXPECTED_ITEMS';
export const UPDATE_INVENTORY_ITEMS_GROUP =
  'storage/UPDATE_INVENTORY_ITEMS_GROUP';
export const UPDATE_EXPECTED_ITEMS_SEARCH =
  'storage/UPDATE_EXPECTED_ITEMS_SEARCH';
export const CREATE_EXPECTED_ITEM = 'storage/CREATE_EXPECTED_ITEM';
export const UPDATE_EXPECTED_ITEM = 'storage/UPDATE_EXPECTED_ITEM';
export const DELETE_EXPECTED_ITEM = 'storage/DELETE_EXPECTED_ITEM';
export const SET_SELECTED_EXPECTED_ITEM_ID =
  'storage/SET_SELECTED_EXPECTED_ITEM_ID';
export const GET_CUSTOMS_DECLARATIONS_FOR_EXPECTED_ITEM =
  'storage/GET_CUSTOMS_DECLARATIONS_FOR_EXPECTED_ITEM';
export const CREATE_CUSTOM_DECLARATION_FOR_EXPECTED_ITEM =
  'storage/CREATE_CUSTOM_DECLARATION_FOR_EXPECTED_ITEM';
export const SELECT_ALL = 'storage/SELECT_ALL';
export const DESELECT_ALL = 'storage/DESELECT_ALL';
export const SELECT_PARCEL = 'storage/SELECT_PARCEL';
export const FETCH_STORAGE = 'storage/FETCH_STORAGE';
export const CATCH_STORAGE_ERROR = 'storage/FETCH_STORAGE_ERROR';
export const UPDATE_STORAGE = 'storage/UPDATE_STORAGE';
export const UPDATE_TRANSFERRING_ITEMS = 'storage/UPDATE_TRANSFERRING_ITEMS';
export const GET_TRANSFERRING_PAGE = 'storage/GET_TRANSFERRING_PAGE';
export const FETCH_TRANSFERRING = 'storage/FETCH_TRANSFERRING';
export const GET_SHIPMENT = 'storage/GET_SHIPMENT';
export const ADD_REQUEST = 'storage/ADD_REQUEST';
export const CREATE_CUSTOM_DECLARATION = 'storage/CREATE_CUSTOM_DECLARATION';
export const GET_SHOPS = 'storage/GET_SHOPS';
export const GET_EXPECTED_SHOPS = 'storage/GET_EXPECTED_SHOPS';
export const UPDATE_ADDONS = 'storage/UPDATE_ADDONS';
export const UPDATE_COMMENT = 'storage/UPDATE_COMMENT';
export const BULK_UPDATE_COMMENT = 'storage/BULK_UPDATE_COMMENT';
export const REMOVE_CONSOLIDATION_FROM_SHIPMENT =
  'storage/REMOVE_CONSOLIDATION_FROM_SHIPMENT';
export const SET_SELECTED_STORAGE_ITEM_ID =
  'storage/SET_SELECTED_STORAGE_ITEM_ID';
export const IMPORT_CUSTOMS_DECLARATION = 'storage/IMPORT_CUSTOMS_DECLARATION';
export const UPDATE_ITEM_COMMENT = 'storage/UPDATE_ITEM_COMMENT';
export const SET_ITEMS_CONSOLIDATION = 'storage/SET_ITEMS_CONSOLIDATION';
export const SEED_DEMO_DETAILED_SHIPMENTS =
  'storage/SEED_DEMO_DETAILED_SHIPMENTS';

// action creators
export const addParcelsToCart = createAction(ADD_PARCELS_TO_CART);
export const finaliseParcelsToCart = createAction(FINALISE_ADD_PARCELS_TO_CART);
export const getStorage = createAction(
  GET_STORAGE,
  (data = defaultParams) => data,
);
export const setActiveStorageBranch = createAction(SET_ACTIVE_STORAGE_BRANCH);
export const getInventoryItems = createAction(
  GET_INVENTORY_ITEMS,
  (data = defaultParamsInventoryItems) => data,
);
/** Loads the next page of a single category. Payload: the category label. */
export const getInventoryItemsPage = createAction(GET_INVENTORY_ITEMS_PAGE);
export const getExpectedItems = createAction(
  GET_EXPECTED_ITEMS,
  (data = defaultParamsExpected) => data,
);
export const updateInventoryItemsGroup = createAction(
  UPDATE_INVENTORY_ITEMS_GROUP,
);
export const updateExpectedItemsSearch = createAction(
  UPDATE_EXPECTED_ITEMS_SEARCH,
);
export const createExpectedItem = createAction(
  CREATE_EXPECTED_ITEM,
  (data) => ({
    promise: api.expected.createExpectedItem({ ...data }),
    data: data,
  }),
);
export const updateExpectedItem = createAction(
  UPDATE_EXPECTED_ITEM,
  (id, data) => ({
    promise: api.expected.updateExpectedItem({ id, ...data }),
    data: data,
  }),
  // This will be passed as meta to reducer (including SUCCESS and ERROR actions)
  (id) => ({
    id: id,
  }),
);
export const deleteExpectedItem = createAction(
  DELETE_EXPECTED_ITEM,
  (id) => ({
    promise: api.expected.deleteExpectedItem(id),
    data: id,
  }), // This will be passed as meta to reducer (including SUCCESS and ERROR actions)
  (id) => ({
    id: id,
  }),
);
export const setSelectedExpectedItemId = createAction(
  SET_SELECTED_EXPECTED_ITEM_ID,
);
export const getCustomsDeclarationsForExpectedItem = createAction(
  GET_CUSTOMS_DECLARATIONS_FOR_EXPECTED_ITEM,
  (data) => ({
    promise: api.expected.getCustomsDeclarationsForExpectedItem(data),
    data,
  }),
);
export const createCustomsDeclarationForExpectedItem = createAction(
  CREATE_CUSTOM_DECLARATION_FOR_EXPECTED_ITEM,
  ({ itemId, data }) => ({
    promise: api.expected.createCustomsDeclarationForExpectedItem({
      itemId,
      data,
    }),
    data: { itemId },
  }),
);

export const selectAll = createAction(SELECT_ALL);
export const deselectAll = createAction(DESELECT_ALL);
export const selectParcel = createAction(SELECT_PARCEL);
export const fetchStorage = createAction(FETCH_STORAGE);
export const catchStorageError = createAction(CATCH_STORAGE_ERROR);
export const updateStorage = createAction(UPDATE_STORAGE);
export const updateTransferringItems = createAction(UPDATE_TRANSFERRING_ITEMS);
export const getTransferringPage = createAction(GET_TRANSFERRING_PAGE);
export const fetchTransferring = createAction(FETCH_TRANSFERRING);
export const addServiceRequest = createAction(ADD_REQUEST);
export const getShipment = createAction(
  GET_SHIPMENT,
  api.shipments.getShipment,
);
export const createCustomsDeclaration = createAction(
  CREATE_CUSTOM_DECLARATION,
  ({ itemId, data, shipmentId, isInventoryItem }) => ({
    promise: isInventoryItem
      ? api.items.replaceCustomsDeclarations({ itemId, data })
      : api.shipments.createCustomsDeclaration({ itemId, data }),
    data: {
      itemId,
      shipmentId: shipmentId || itemId,
      isInventoryItem: Boolean(isInventoryItem),
    },
  }),
);

export const getShops = createAction(GET_SHOPS, api.shipments.getShops);

export const getExpectedShops = createAction(
  GET_EXPECTED_SHOPS,
  api.shipments.getExpectedShops,
);
export const updateAddons = createAction(
  UPDATE_ADDONS,
  (id, data, inventoryId) => ({
    promise: api.shipments.updateAddons(id, data, inventoryId),
  }),
  (id, data, inventoryId) => ({
    id,
    data,
    inventoryId,
  }),
);
export const updateComment = createAction(
  UPDATE_COMMENT,
  api.shipments.updateComment,
);

export const bulkUpdateComment = createAction(
  BULK_UPDATE_COMMENT,
  (data) => api.shipments.bulkUpdateComment(data),
  (data) => ({
    ids: data.ids,
    customer_comment: data.customer_comment,
  }),
);
export const updateItemComment = createAction(
  UPDATE_ITEM_COMMENT,
  (id, data) => ({
    promise: api.items.updateItemComment(id, data),
  }),
  // This will be passed as meta to reducer (including SUCCESS and ERROR actions)
  (id, data) => ({
    inventoryItemId: id,
  }),
);
export const removeConsolidationFromShipment = createAction(
  REMOVE_CONSOLIDATION_FROM_SHIPMENT,
);
export const setSelectedStorageItemId = createAction(
  SET_SELECTED_STORAGE_ITEM_ID,
);
export const setItemsConsolidation = createAction(SET_ITEMS_CONSOLIDATION);
export const seedDemoDetailedShipments = createAction(
  SEED_DEMO_DETAILED_SHIPMENTS,
);
export const importCustomsDeclaration = createAction(
  IMPORT_CUSTOMS_DECLARATION,
  ({ id, file }) => {
    const data = new FormData();
    data.append('customs_declaration_import[file]', file);
    return {
      promise: api.shipments.importCustomsDeclaration({ id, data }),
    };
  },
);

// helpers — API may expose totals as a header object (axios) or [[key, val], ...] pairs
const readTotalsFromHeaders = (headersPayload) => {
  if (!headersPayload || typeof headersPayload.toJS !== 'function') {
    return { total: '0', weight: '0' };
  }
  const h = headersPayload.toJS();
  if (Array.isArray(h)) {
    return {
      total: String(
        h.find((x) => Array.isArray(x) && x[0] === 'total')?.[1] ?? 0,
      ),
      weight: String(
        h.find((x) => Array.isArray(x) && x[0] === 'total-weight')?.[1] ?? 0,
      ),
    };
  }
  if (h && typeof h === 'object') {
    return {
      total: String(h.total ?? h.Total ?? 0),
      weight: String(
        h['total-weight'] ?? h['Total-Weight'] ?? h.total_weight ?? 0,
      ),
    };
  }
  return { total: '0', weight: '0' };
};

// The items feed mixes real inventory items ("I…" SKUs) with shipment-level
// rows ("S…" SKUs — same prefix rule as StorageItemRow/StorageItemPanel); the
// Items view shows inventory items only.
const isInventoryItemSku = (item) => {
  const sku = item.get('sku');
  return typeof sku === 'string' && sku.startsWith('I');
};

// Applies `updater` to the items of every loaded category. The items list is
// split across categories, but edits (comments, consolidation) address an item
// wherever it happens to sit.
// The list rows carry `has_customs_declarations`; once a shipment's details
// are in the store they are the source of truth, so mirror them onto the row
// after a declaration save or a detail refetch.
const syncHasCustomsDeclarations = (state, shipmentId) => {
  const items = state.getIn(['detailedShipment', shipmentId, 'items']);
  if (!items) return state;

  const hasDeclarations = items.some(
    (item) => item.get('customs_declarations', List()).size > 0,
  );

  return ['items', 'transferringItems'].reduce((newState, key) => {
    const rows = newState.get(key);
    const index = rows
      ? rows.findIndex((row) => row.get('id') === shipmentId)
      : -1;
    return index === -1
      ? newState
      : newState.setIn(
          [key, index, 'has_customs_declarations'],
          hasDeclarations,
        );
  }, state);
};

const updateItemsInGroups = (state, updater) =>
  state.update('inventoryItemsGroups', (groups) =>
    groups.map((group) => group.update('items', updater)),
  );

export const normalizeItems = (items) =>
  items.map((item) => {
    const serviceRequestCount = {};

    item.get('service_requests_count', List()).forEach((request) => {
      const reqPropName = request.get(0).toLowerCase();
      serviceRequestCount[reqPropName] = request.get(1);
    });

    return item
      .delete('service_requests_count')
      .set('serviceRequestCount', Map(serviceRequestCount));
  });

export const normalizeDetailedShipment = (shipment) =>
  shipment
    .update('service_requests', (requests) =>
      requests.map((request) =>
        request
          .updateIn(['service_request_type', 'type'], (type) =>
            type.toLowerCase(),
          )
          .update('pictures', (pictures) => normalizeImages(pictures)),
      ),
    )
    .update('pictures', (pictures) => {
      const serviceRequests = shipment.get('service_requests', List());
      const serviceRequest = serviceRequests.find(
        (request) =>
          request.getIn(['service_request_type', 'type']) ===
          'Additional photos',
      );
      const picturesServiceRequest = serviceRequest
        ? normalizeImages(serviceRequest.get('pictures'))
        : [];

      return pictures.merge(picturesServiceRequest);
    })
    .updateIn(['items'], (items) =>
      items.map((item) =>
        item.set('pictures', normalizeImages(item.get('pictures'))),
      ),
    );

const storageReducer = handleActions(
  {
    [GET_STORAGE]: (state, { payload }) =>
      payload.size ? state : state.set('items', null),

    [SET_ACTIVE_STORAGE_BRANCH]: (state, { payload }) => {
      return state.set('activeStorageBranch', payload);
    },

    [FETCH_STORAGE]: (state, { payload }) => {
      const isPagination =
        state.getIn(['params', 'page']) < payload.get('page');
      const isAllFetch =
        state.get('totalAmount') === payload.getIn(['filter', 'per_page']);
      const shouldDropSelected = payload.get('isAddParcelsToCart')
        ? false
        : !(isPagination || isAllFetch);
      const selected = shouldDropSelected ? List() : state.get('selected');

      return state.set('selected', selected).set('isLoading', true);
    },

    [FINALISE_ADD_PARCELS_TO_CART]: (state) => {
      return state.set('selected', List());
    },

    [UPDATE_STORAGE]: (state, { payload }) => {
      const isPagination =
        state.getIn(['params', 'page']) < payload.getIn(['params', 'page']);
      const { total: totalStr, weight: weightStr } = readTotalsFromHeaders(
        payload.get('headers'),
      );

      const totalAmount =
        payload.get('headers') && !isPagination
          ? parseInt(totalStr, 10)
          : state.get('totalAmount');
      const totalWeight = payload.get('headers') && parseFloat(weightStr);

      const normalizedItems = normalizeItems(payload.get('data'));
      const hasItems = !!normalizedItems.size;
      return state
        .update('items', (items) =>
          isPagination ? items.concat(normalizedItems) : normalizedItems,
        )
        .set('totalWeight', totalWeight)
        .set('totalAmount', totalAmount)
        .set('hasItems', hasItems)
        .set('params', payload.get('params'))
        .set('isLoading', false);
    },

    [FETCH_TRANSFERRING]: (state) => state.set('isLoading', true),

    // Payload is the API response tagged with the page it belongs to; the
    // first page replaces the list, later pages append.
    [UPDATE_TRANSFERRING_ITEMS]: (state, { payload }) => {
      const page = payload.get('page', 1);
      const { total } = readTotalsFromHeaders(payload.get('headers'));
      const normalizedItems = normalizeItems(payload.get('data'));

      return state
        .update('transferringItems', (items) =>
          page > 1 ? items.concat(normalizedItems) : normalizedItems,
        )
        .set('transferringTotal', parseInt(total, 10))
        .set('transferringPage', page)
        .set('isLoading', false);
    },

    [GET_INVENTORY_ITEMS]: (state, { payload }) => {
      const groups = List(payload.get('groups', DEFAULT_ITEM_STATUS_GROUPS));
      const previous = state.get('inventoryItemsGroups', Map());

      // Carry over data for categories that stay on screen so the list
      // revalidates silently instead of blanking; categories this request drops
      // disappear at once. The filter is kept for per-category pagination.
      return state
        .set('isLoading', true)
        .set('inventoryItemsFilter', fromJS(payload.get('filter') || {}))
        .set(
          'inventoryItemsGroups',
          Map(
            groups.map((group) => [
              group,
              previous.get(group, defaultInventoryItemsGroup),
            ]),
          ),
        );
    },

    [GET_INVENTORY_ITEMS_PAGE]: (state) => state.set('isLoading', true),

    [GET_EXPECTED_ITEMS]: (state, { payload }) => {
      return state.set('isLoading', true);
    },

    [UPDATE_INVENTORY_ITEMS_GROUP]: (state, { payload }) => {
      const group = payload.get('group');
      const page = payload.get('page');
      const headers = payload.get('headers');
      const { total: totalStr } = readTotalsFromHeaders(headers);
      const data = payload
        .get('data')
        .filter(isInventoryItemSku)
        .map((item) => item.update('pictures', normalizeImages));
      const removed = payload.get('data').size - data.size;

      return state
        .updateIn(['inventoryItemsGroups', group], (current) => {
          const previous = current || defaultInventoryItemsGroup;
          // The header total counts the hidden rows too; subtracting the rows
          // dropped so far keeps the title count honest and lets the has-more
          // check (total > items.size) still see the unfetched pages.
          const filteredOut =
            page > 1 ? previous.get('filteredOut', 0) + removed : removed;

          return previous
            .update('items', (items) => (page > 1 ? items.concat(data) : data))
            .set('filteredOut', filteredOut)
            .set(
              'total',
              headers
                ? Math.max(parseInt(totalStr, 10) - filteredOut, 0)
                : previous.get('total'),
            )
            .set('page', page);
        })
        .set('isLoading', false);
    },

    [UPDATE_EXPECTED_ITEMS_SEARCH]: (state, { payload }) => {
      const isPagination = payload.getIn(['params', 'page']) > 1;

      const data = payload.get('data');
      const headers = payload.get('headers');
      const totalHeader = headers.find(
        (headerArray) => headerArray.get(0) === 'total',
      );

      const totalCount = totalHeader ? parseInt(totalHeader.get(1)) : 0;

      return state
        .update('expectedItemsSearch', (expectedItems) =>
          isPagination ? expectedItems.concat(data) : data,
        )
        .set('isLoading', false)
        .set('expectedItemsTotalAmount', totalCount);
    },

    [`${CREATE_EXPECTED_ITEM}_SUCCESS`]: (state, { payload }) => {
      return state.set(
        'expectedItemsSearch',
        state.get('expectedItemsSearch').push(payload.get('data')),
      );
    },

    [`${UPDATE_EXPECTED_ITEM}_SUCCESS`]: (state, { payload, meta }) => {
      const data = payload.get('data');
      return state.set(
        'expectedItemsSearch',
        state.get('expectedItemsSearch').map((item) => {
          return item.get('id') === data.get('id') ? data : item;
        }),
      );
    },

    [`${DELETE_EXPECTED_ITEM}_SUCCESS`]: (state, { meta }) => {
      const id = meta.id;

      return updateIn(state, ['expectedItemsSearch'], (expectedItemsSearch) =>
        expectedItemsSearch.filter((item) => item.get('id') !== id),
      );
    },

    [`${GET_CUSTOMS_DECLARATIONS_FOR_EXPECTED_ITEM}_REQUEST`]: (
      state,
      { payload },
    ) =>
      state
        .set('tempExpectedItemId', payload)
        .set('isLoadingCustomsDeclaration', true),

    [`${GET_CUSTOMS_DECLARATIONS_FOR_EXPECTED_ITEM}_SUCCESS`]: (
      state,
      { payload },
    ) => {
      const itemId = state.get('tempExpectedItemId');
      return updateIn(state, ['expectedItemsSearch'], (expectedItemsSearch) =>
        expectedItemsSearch.map((item) =>
          item.get('id') !== itemId
            ? item
            : item.set('customs_declarations', payload.get('data')),
        ),
      ).set('isLoadingCustomsDeclaration', false);
    },

    [combineActions(`${CREATE_CUSTOM_DECLARATION_FOR_EXPECTED_ITEM}_REQUEST`)]:
      (state, { payload }) => state.set('tempExpectedItemId', payload.itemId),

    [`${CREATE_CUSTOM_DECLARATION_FOR_EXPECTED_ITEM}_SUCCESS`]: (
      state,
      { payload },
    ) => {
      const itemId = state.get('tempExpectedItemId');

      return updateIn(state, ['expectedItemsSearch'], (expectedItemsSearch) =>
        expectedItemsSearch.map((item) => {
          if (item.get('id') === itemId) {
            const newList = new List(item.get('customs_declarations'));
            newList.push(payload.get('data'));

            const newItem = new Map(item).merge({
              customs_declarations: newList,
            });
            return newItem;
          }

          return item;
        }),
      );
    },

    [CATCH_STORAGE_ERROR]: (state) => state.set('isLoading', false),

    [DESELECT_ALL]: (state) => state.set('selected', List()),

    [SELECT_PARCEL]: (state, { payload }) => {
      if (List.isList(payload)) return state.set('selected', payload);

      return state.update('selected', (selected) => {
        const index = selected.indexOf(payload);
        return index === -1 ? selected.push(payload) : selected.delete(index);
      });
    },

    /* DETAILED SHIPMENT */
    [combineActions(
      `${GET_SHIPMENT}_REQUEST`,
      `${UPDATE_ADDONS}_REQUEST`,
      `${IMPORT_CUSTOMS_DECLARATION}_REQUEST`,
    )]: (state) => state.set('isShipmentLoading', true),

    [`${GET_SHIPMENT}_SUCCESS`]: (state, { payload }) => {
      const data = normalizeDetailedShipment(payload.get('data'));

      return syncHasCustomsDeclarations(
        state
          .setIn(['detailedShipment', data.get('id')], data)
          .set('isShipmentLoading', false),
        data.get('id'),
      );
    },

    // Demo shipments shown to empty accounts: pre-seed the detail cache so the
    // right panel renders them without ever calling the backend.
    [SEED_DEMO_DETAILED_SHIPMENTS]: (state, { payload }) =>
      payload.reduce(
        (newState, shipment) =>
          newState.setIn(['detailedShipment', shipment.get('id')], shipment),
        state,
      ),

    [`${UPDATE_ADDONS}_SUCCESS`]: (state, { payload, meta }) => {
      const data = payload.get('data');

      if (meta && meta.id && meta.inventoryId) {
        return state
          .updateIn(['detailedShipment', meta.id], (detailedShipment) =>
            detailedShipment.update('items', (items) => {
              return items.map((item) => {
                if (item.get('id') === meta.inventoryId) {
                  const newList = List(meta.data.addon_ids);
                  return item.set('addons', newList);
                } else {
                  return item;
                }
              });
            }),
          )
          .set('isShipmentLoading', false);
      }

      return state
        .update('items', (items) =>
          items.map((item) =>
            item.get('id') === data.get('id')
              ? item.set('addons', data.get('addons'))
              : item,
          ),
        )
        .updateIn(['detailedShipment', data.get('id')], (shipment) =>
          shipment.set('addons', data.get('addons')),
        )
        .set('isShipmentLoading', false);
    },

    [`${UPDATE_COMMENT}_SUCCESS`]: (state, { payload }) => {
      const id = payload.getIn(['data', 'id']);
      const comment = payload.getIn(['data', 'customer_comment']);
      let index = state.get('items').findIndex((item) => item.get('id') === id);
      if (index !== -1) {
        return state
          .setIn(['items', index, 'customer_comment'], comment)
          .setIn(['detailedShipment', id, 'customer_comment'], comment);
      } else {
        index = state
          .get('transferringItems')
          .findIndex((item) => item.get('id') === id);
        return state
          .setIn(['transferringItems', index, 'customer_comment'], comment)
          .setIn(['detailedShipment', id, 'customer_comment'], comment);
      }
    },

    [`${BULK_UPDATE_COMMENT}_SUCCESS`]: (state, { meta }) => {
      const { ids, customer_comment } = meta;

      let newState = state;
      ids.forEach((id) => {
        const index = newState
          .get('items')
          .findIndex((item) => item.get('id') === id);
        if (index !== -1) {
          newState = newState.setIn(
            ['items', index, 'customer_comment'],
            customer_comment,
          );
        }
        if (newState.getIn(['detailedShipment', id])) {
          newState = newState.setIn(
            ['detailedShipment', id, 'customer_comment'],
            customer_comment,
          );
        }
      });
      return newState;
    },

    [`${UPDATE_ITEM_COMMENT}_SUCCESS`]: (state, { payload, meta }) => {
      const data = payload.get('data');
      const index = data
        .get('items')
        .findIndex((item) => item.get('id') === meta.inventoryItemId);
      const comment = data.getIn(['items', index, 'customer_comment']);

      return updateItemsInGroups(
        state.setIn(
          [
            'detailedShipment',
            data.get('id'),
            'items',
            index,
            'customer_comment',
          ],
          comment,
        ),
        (items) =>
          items.map((item) =>
            item.get('id') === meta.inventoryItemId
              ? item.set('customer_comment', comment)
              : item,
          ),
      );
    },

    [`${REMOVE_CONSOLIDATION_FROM_SHIPMENT}`]: (state, { payload }) => {
      return state
        .updateIn(['detailedShipment', payload], (shipment) =>
          shipment.delete('consolidation'),
        )
        .update('items', (items) =>
          items.map((item) =>
            item.get('id') !== payload ? item : item.delete('consolidation_id'),
          ),
        );
    },

    [SET_ITEMS_CONSOLIDATION]: (state, { payload }) => {
      const { shipmentId, itemIds, consolidation } = payload;
      const setConsolidation = (items) =>
        (items || List()).map((item) =>
          itemIds.includes(item.get('id'))
            ? item.set('consolidation', fromJS(consolidation))
            : item,
        );

      const newState = updateItemsInGroups(state, setConsolidation);
      if (!newState.getIn(['detailedShipment', shipmentId])) {
        return newState;
      }
      return newState.updateIn(
        ['detailedShipment', shipmentId, 'items'],
        setConsolidation,
      );
    },

    [combineActions(
      `${GET_SHIPMENT}_ERROR`,
      `${UPDATE_ADDONS}_ERROR`,
      `${IMPORT_CUSTOMS_DECLARATION}_SUCCESS`,
      `${IMPORT_CUSTOMS_DECLARATION}_ERROR`,
    )]: (state) => state.set('isShipmentLoading', false),

    /* SERVICE REQUESTS */
    [ADD_REQUEST]: (state, { payload }) => {
      const packageId = payload.get('packageId');
      let data = payload.get('data');

      // Uppercase request type
      const typeUppercase = data.getIn(['service_request_type', 'type']);
      // Need to convert to lowercase
      const type = typeUppercase.toLowerCase();

      data = data.setIn(['service_request_type', 'type'], type);
      const isRequestCanceled = data.get('state') === 'canceled';
      const items = state.get('items').map((item) => {
        if (item.get('id') !== packageId) return item;

        if (isRequestCanceled) {
          const requestNameToDelete = type;
          const serviceRequestCount = item
            .get('serviceRequestCount')
            .delete(requestNameToDelete);

          return item.set('serviceRequestCount', serviceRequestCount);
        }

        const requestName = type;
        return item.setIn(['serviceRequestCount', requestName], 'unprocessed');
      });

      return updateIn(
        state,
        ['detailedShipment', packageId, 'service_requests'],
        (service_requests) => {
          const id = data.get('id');
          const index = service_requests.findIndex(
            (request) => request.get('id') === id,
          );

          if (isRequestCanceled) return service_requests.delete(index);

          return index === -1
            ? service_requests.push(data)
            : service_requests.set(index, data);
        },
      ).set('items', items);
    },

    [`${CREATE_REQUEST_TRANSFER_SHIPMENT}_SUCCESS`]: (state, { meta }) => {
      const { ids } = meta;

      const transferredItemsList = state
        .get('items')
        .filter((x) => ids.includes(x.get('id')))
        .map((item) => item.set('state', SHIPMENT_TRANSFERRING_STATE));

      return state
        .update('items', (items) =>
          items.filter((x) => !ids.includes(x.get('id'))),
        )
        .update(
          'totalAmount',
          (totalAmount) => totalAmount - transferredItemsList.size,
        )
        .update(
          'totalWeight',
          (totalWeight) =>
            totalWeight -
            transferredItemsList.reduce((sum, x) => sum + x.get('weight'), 0),
        );
    },

    [`${COMPLETE_REQUEST_TRANSFER_SHIPMENTS}_SUCCESS`]: (state, { meta }) => {
      return state;
    },

    /* CUSTOM DECLARATION */
    [combineActions(`${CREATE_CUSTOM_DECLARATION}_REQUEST`)]: (
      state,
      { payload },
    ) =>
      state
        .set('tempCustomsDeclarationIsInventoryItem', payload.isInventoryItem)
        .set('tempDetailedItemId', payload.itemId)
        .set('tempDetailedShipmentId', payload.shipmentId || payload.itemId),

    [`${CREATE_CUSTOM_DECLARATION}_SUCCESS`]: (state, { payload }) => {
      const itemId = state.get('tempDetailedItemId');
      const shipmentId = state.get('tempDetailedShipmentId');
      const isInventoryItem = state.get(
        'tempCustomsDeclarationIsInventoryItem',
      );

      const items = state.getIn(['detailedShipment', shipmentId, 'items']);
      let itemIndex = 0;
      if (isInventoryItem && items) {
        const foundIndex = items.findIndex((item) => item.get('id') === itemId);
        if (foundIndex !== -1) itemIndex = foundIndex;
      }

      return syncHasCustomsDeclarations(
        updateIn(
          state,
          [
            'detailedShipment',
            shipmentId,
            'items',
            itemIndex,
            'customs_declarations',
          ],
          () => payload.get('data'),
        ),
        shipmentId,
      );
    },

    /* VENDORS */
    [`${GET_SHOPS}_REQUEST`]: (state) => state.set('isShopsLoading', true),

    [`${GET_SHOPS}_SUCCESS`]: (state, { payload }) =>
      state.set('shops', payload.get('data')).set('isShopsLoading', false),

    [`${GET_SHOPS}_ERROR`]: (state) => state.set('isShopsLoading', false),

    [`${GET_EXPECTED_SHOPS}_REQUEST`]: (state) =>
      state.set('isExpectedShopsLoading', true),

    [`${GET_EXPECTED_SHOPS}_SUCCESS`]: (state, { payload }) =>
      state
        .set('expectedShops', payload.get('data'))
        .set('isExpectedShopsLoading', false),

    [`${GET_EXPECTED_SHOPS}_ERROR`]: (state) =>
      state.set('isExpectedShopsLoading', false),

    /* SELECTED ITEMS */
    [SET_SELECTED_STORAGE_ITEM_ID]: (state, { payload }) =>
      state.set('selectedStorageItemId', payload),

    [SET_SELECTED_EXPECTED_ITEM_ID]: (state, { payload }) =>
      state.set('selectedExpectedItemId', payload),

    ['rightPanel/CLOSE_PANEL']: (state) =>
      state.set('selectedStorageItemId', null),
  },

  initialState,
);

export default storageReducer;
